(() => {
  'use strict';
  const body = document.body;
  const html = document.documentElement;
  if (!body || !body.classList.contains('itp-v14') || window.ITPImmersiveV14) return;
  const scenes = new Map();
  const dialogs = new Map();
  const details = new Map();
  const near = new Set();
  const cleanups = [];
  const animations = new Set();
  const openerState = new Map();
  const clamp = (value, low = 0, high = 1) => Math.max(low, Math.min(high, value));
  const registry = document.querySelector('[data-immersive-registry]');
  let frame = 0;
  let disposed = false;
  let frozen = false;
  let observer;
  let effective = false;
  let currentSettings;
  const setting = (name, fallback) => body.dataset[name] ?? registry?.dataset[name] ?? fallback;
  const settings = () => ({
    enabled: setting('immersiveEnabled', 'true') !== 'false',
    intensity: ({ subtle: .55, balanced: 1, cinematic: 1.4 })[setting('immersiveIntensity', 'balanced')] || 1,
    duration: clamp(Number(setting('immersiveDuration', '650')) || 650, 200, 1800),
    depth: setting('immersiveDepth', 'true') !== 'false',
    quality: setting('immersiveQuality', 'auto') === 'light' ? 'light' : 'auto'
  });
  const allowed = config => config.enabled && html.dataset.motion === 'on' && html.dataset.readingCalm !== 'on';
  const paused = () => document.hidden || frozen || body.classList.contains('menu-open') || !!document.querySelector('dialog[open]');
  const listen = (target, type, callback, options, list = cleanups) => {
    target.addEventListener(type, callback, options);
    list.push(() => target.removeEventListener(type, callback, options));
  };
  const runAnimation = (node, keyframes, duration) => {
    if (!node || !effective || document.hidden || typeof node.animate !== 'function') return null;
    try {
      const animation = node.animate(keyframes, { duration, easing: 'cubic-bezier(.2,.75,.2,1)', fill: 'none' });
      animations.add(animation);
      animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
      return animation;
    } catch { return null; }
  };
  const cancelAnimation = animation => { if (animation) { animations.delete(animation); animation.cancel(); } };
  const removeProperties = (node, names) => names.forEach(name => node.style.removeProperty(name));
  const intrinsicCopyHeight = node => {
    const boxes = [...node.children].filter(child => child.getClientRects().length).map(child => child.getBoundingClientRect()).filter(box => box.height > 0);
    if (!boxes.length) return node.scrollHeight || node.getBoundingClientRect().height;
    const style = getComputedStyle(node);
    return Math.max(...boxes.map(box => box.bottom)) - Math.min(...boxes.map(box => box.top)) + (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
  };
  const resetScene = record => {
    record.node.removeAttribute('data-immersive-enhanced');
    record.node.removeAttribute('data-immersive-light');
    record.node.removeAttribute('data-immersive-active');
    record.node.removeAttribute('data-immersive-progress');
    removeProperties(record.node, ['--immersive-progress', '--immersive-top']);
    for (const item of record.media) {
      removeProperties(item.node, ['--immersive-pan', '--immersive-scale', '--immersive-opacity']);
      item.node.removeAttribute('data-immersive-active');
    }
    for (const item of record.copy) {
      removeProperties(item.node, ['--immersive-copy-shift', '--immersive-copy-opacity']);
      item.node.removeAttribute('data-immersive-active');
    }
    for (const progress of record.progress) progress.style.removeProperty('--immersive-progress');
  };
  const destroyScene = record => {
    resetScene(record);
    observer?.unobserve(record.node);
    near.delete(record);
    record.node.removeAttribute('data-immersive-ready');
    record.excludes.forEach(name => record.node.removeAttribute(name));
    record.media.forEach(item => item.image?.removeAttribute('data-immersive-image-owned'));
    record.copy.forEach(item => item.node.removeAttribute('data-immersive-copy-owned'));
    scenes.delete(record.node);
  };
  const initScene = node => {
    if (body.classList.contains('itp-v15') || !observer || scenes.has(node) || node.closest('dialog,[inert]')) return;
    const stage = node.querySelector('[data-immersive-stage]');
    if (!stage) return;
    const media = [...node.querySelectorAll('[data-immersive-media]')].map(element => {
      const image = element.querySelector('img');
      const style = image ? getComputedStyle(image) : null;
      const ownsImage = !!image && style.transform === 'none' && (!style.scale || style.scale === 'none') && (!style.translate || style.translate === 'none') && !image.hasAttribute('data-v12-image');
      if (ownsImage) image.dataset.immersiveImageOwned = 'true';
      return { node: element, image, ownsImage };
    });
    const copy = [...node.querySelectorAll('[data-immersive-copy]')].map(element => {
      const style = getComputedStyle(element);
      const ownsCopy = style.transform === 'none' && (!style.translate || style.translate === 'none') && !element.hasAttribute('data-v12-reveal') && !element.hasAttribute('data-v13-state');
      if (ownsCopy) element.dataset.immersiveCopyOwned = 'true';
      return { node: element, ownsCopy, opacity: Number.isFinite(Number(style.opacity)) ? Number(style.opacity) : 1 };
    });
    const record = { node, stage, media, copy, progress: [...node.querySelectorAll('[data-immersive-progress]')], selected: false, excludes: ['data-v12-motion-exclude', 'data-v13-motion-exclude'].filter(name => !node.hasAttribute(name)) };
    scenes.set(node, record);
    node.dataset.immersiveReady = 'true';
    node.setAttribute('data-v12-motion-exclude', '');
    node.setAttribute('data-v13-motion-exclude', '');
    observer?.observe(node);
    if (node.getBoundingClientRect().top < innerHeight && node.getBoundingClientRect().bottom > 0) near.add(record);
  };
  const updateScenes = () => {
    frame = 0;
    if (disposed || document.hidden || frozen) return;
    synchronize();
    if (!effective || paused()) return;
    const active = [...near].filter(record => record.node.isConnected).slice(0, 12);
    const header = document.querySelector('.site-header');
    const top = Math.max(0, header?.getBoundingClientRect().height || 0) + 12;
    const measurements = active.map(record => ({ record, bounds: record.node.getBoundingClientRect(), stage: record.stage.getBoundingClientRect(), copyHeight: Math.max(0, ...record.copy.map(item => intrinsicCopyHeight(item.node))) }));
    let current;
    for (const measurement of measurements) {
      const { record, bounds, stage, copyHeight } = measurement;
      if (record.selected) { resetScene(record); continue; }
      const light = currentSettings.quality === 'light' || innerWidth < 900 || innerHeight < 620 || ['large', 'larger'].includes(html.dataset.readingSize);
      const fits = copyHeight < Math.max(160, innerHeight - top - 48);
      const enhanced = !light && fits && record.node.closest('[data-story-sticky]')?.dataset.storySticky !== 'false';
      if (enhanced) record.node.dataset.immersiveEnhanced = 'true';
      else record.node.removeAttribute('data-immersive-enhanced');
      record.node.dataset.immersiveLight = String(light || !fits);
      record.node.style.setProperty('--immersive-top', `${top.toFixed(1)}px`);
      const progress = enhanced ? clamp((top - bounds.top) / Math.max(1, bounds.height - stage.height)) : clamp((innerHeight - bounds.top) / Math.max(1, innerHeight + bounds.height));
      record.node.style.setProperty('--immersive-progress', progress.toFixed(4));
      record.node.dataset.immersiveProgress = String(Math.round(progress * 100));
      const distance = Math.abs(bounds.top + bounds.height / 2 - innerHeight / 2);
      if (!current || distance < current.distance) current = { record, distance };
      const visual = record.node.dataset.immersiveVisual || 'depth';
      record.media.forEach((item, index) => {
        const position = record.media.length > 1 ? index / (record.media.length - 1) : .5;
        const weight = record.media.length > 1 && visual === 'crossfade' ? clamp(1 - Math.abs(progress - position) * (record.media.length - 1)) : 1;
        const strength = currentSettings.intensity * (light ? .3 : 1);
        const moving = currentSettings.depth && visual !== 'still';
        item.node.style.setProperty('--immersive-pan', `${(moving ? (.5 - progress) * 38 * strength : 0).toFixed(2)}px`);
        item.node.style.setProperty('--immersive-scale', (moving ? 1.035 + Math.abs(progress - .5) * .045 * strength : 1).toFixed(4));
        item.node.style.setProperty('--immersive-opacity', (record.media.length > 1 ? .15 + .85 * weight : 1).toFixed(4));
        item.node.dataset.immersiveActive = String(record.media.length === 1 || weight > .5);
      });
      let activeCopy = Math.round(progress * Math.max(0, record.copy.length - 1));
      record.copy.forEach((item, index) => { if (item.node.contains(document.activeElement)) activeCopy = index; });
      record.copy.forEach((item, index) => {
        const selected = activeCopy === index;
        item.node.dataset.immersiveActive = String(selected);
        item.node.style.setProperty('--immersive-copy-opacity', (item.opacity * (selected ? 1 : .68)).toFixed(4));
        item.node.style.setProperty('--immersive-copy-shift', `${(item.node.contains(document.activeElement) ? 0 : (.5 - progress) * (light ? 8 : 16) * currentSettings.intensity).toFixed(2)}px`);
      });
      for (const progressNode of record.progress) progressNode.style.setProperty('--immersive-progress', progress.toFixed(4));
    }
    for (const { record } of measurements) if (!record.selected) record.node.dataset.immersiveActive = String(current?.record === record);
  };
  function schedule() { if (!disposed && !frame && !document.hidden && !frozen) frame = requestAnimationFrame(updateScenes); }
  const dialogId = opener => (opener.dataset.immersiveDialogOpen || opener.dataset.modal || '').replace(/^#/, '');
  const findDialog = id => [...dialogs.values()].find(record => record.node.id === id);
  const restoreFocus = record => {
    if (!record.disposed && record.opener?.isConnected && record.opener.getClientRects().length) record.opener.focus({ preventScroll: true });
    record.opener = null;
  };
  const finishClose = record => {
    record.version++;
    cancelAnimation(record.animation);
    record.animation = null;
    if (record.node.open) record.node.close();
    record.node.dataset.immersiveDialogState = 'closed';
  };
  const closeDialog = (record, immediate = false) => {
    if (!record || !record.node.open) return;
    const version = ++record.version;
    cancelAnimation(record.animation);
    record.node.dataset.immersiveDialogState = 'closing';
    const animation = immediate ? null : runAnimation(record.panel, [{ opacity: 1, translate: '0 0px', scale: '1' }, { opacity: 0, translate: '0 12px', scale: '.985' }], Math.min(380, currentSettings.duration * .48));
    record.animation = animation;
    if (!animation) { finishClose(record); return; }
    const finish = () => { if (record.version === version && !record.disposed) finishClose(record); };
    animation.finished.then(finish, finish);
  };
  const fillLightbox = (record, opener) => {
    if (!record.node.hasAttribute('data-immersive-lightbox')) return true;
    const original = opener.querySelector('img');
    const value = opener.dataset.modalImage || opener.getAttribute('href') || original?.currentSrc || original?.getAttribute('src');
    if (typeof value !== 'string' || !value.trim()) return false;
    let source;
    try { source = new URL(value, location.href); if (!/^https?:$/.test(source.protocol)) return false; } catch { return false; }
    const slot = record.node.querySelector('[data-immersive-lightbox-slot]');
    if (!slot) return false;
    const image = document.createElement('img');
    image.alt = opener.dataset.modalAlt || original?.alt || '';
    image.decoding = 'async';
    image.src = source.href;
    slot.replaceChildren(image);
    const caption = record.node.querySelector('[data-immersive-lightbox-caption]');
    if (caption) caption.textContent = opener.dataset.modalCaption || image.alt;
    const title = record.node.querySelector('[data-immersive-lightbox-title]');
    if (title) title.textContent = opener.dataset.modalTitle || 'INTHATPACK';
    return true;
  };
  const openDialog = (record, opener) => {
    if (!record || record.disposed || typeof record.node.showModal !== 'function' || !fillLightbox(record, opener)) return false;
    if (record.node.open) return true;
    try { record.node.showModal(); } catch { return false; }
    record.opener = opener;
    const version = ++record.version;
    record.node.dataset.immersiveDialogState = 'opening';
    record.animation = runAnimation(record.panel, [{ opacity: .4, translate: '0 20px', scale: '.975' }, { opacity: 1, translate: '0 0px', scale: '1' }], Math.min(700, currentSettings.duration));
    const focus = record.node.querySelector('[data-modal-focus]') || record.node.querySelector('[autofocus]') || record.node.querySelector('h2,h1') || record.node.querySelector('[data-immersive-dialog-close],[data-modal-close]');
    if (focus) {
      if (focus.matches('h1,h2') && !focus.hasAttribute('tabindex')) { focus.setAttribute('tabindex', '-1'); record.focusTarget = focus; }
      focus.focus({ preventScroll: true });
    }
    if (record.animation) record.animation.finished.then(() => { if (record.version === version && !record.disposed) record.node.dataset.immersiveDialogState = 'open'; }, () => {});
    else record.node.dataset.immersiveDialogState = 'open';
    return true;
  };
  const initDialog = node => {
    if (dialogs.has(node) || typeof node.showModal !== 'function') return;
    const cleanup = [];
    const source = [...document.querySelectorAll('[data-immersive-modal-source]')].find(item => item.dataset.immersiveModalSource === node.id);
    const content = source?.querySelector('[data-immersive-source-content]');
    const slot = node.querySelector('[data-immersive-modal-slot]');
    if (content && slot) slot.append(content);
    const record = { node, panel: node.querySelector('[data-immersive-dialog-panel]') || node, source, content, cleanup, opener: null, animation: null, version: 0, disposed: false };
    dialogs.set(node, record);
    node.dataset.immersiveDialogReady = 'true';
    node.dataset.immersiveDialogState = 'closed';
    if (source) { source.dataset.immersiveSourceReady = 'true'; source.open = false; }
    listen(node, 'cancel', event => { if (effective) { event.preventDefault(); closeDialog(record); } }, false, cleanup);
    listen(node, 'close', () => {
      record.version++;
      cancelAnimation(record.animation);
      record.animation = null;
      node.dataset.immersiveDialogState = 'closed';
      if (node.hasAttribute('data-immersive-lightbox')) node.querySelector('[data-immersive-lightbox-slot]')?.replaceChildren();
      restoreFocus(record);
      schedule();
    }, false, cleanup);
    listen(node, 'click', event => {
      if (event.target !== node) return;
      const rect = node.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(record);
    }, false, cleanup);
  };
  const destroyDialog = record => {
    record.disposed = true;
    record.opener = null;
    finishClose(record);
    record.cleanup.forEach(remove => remove());
    if (record.source && record.content) {
      record.source.append(record.content);
      record.source.removeAttribute('data-immersive-source-ready');
    }
    record.node.removeAttribute('data-immersive-dialog-ready');
    record.node.removeAttribute('data-immersive-dialog-state');
    record.focusTarget?.removeAttribute('tabindex');
    dialogs.delete(record.node);
  };
  const enhanceOpeners = scope => {
    scope.querySelectorAll('[data-immersive-dialog-open],[data-modal],[data-modal-image]').forEach(opener => {
      const id = opener.hasAttribute('data-modal-image') ? dialogId(opener) || 'ITPImmersiveImage' : dialogId(opener);
      if (!findDialog(id)) return;
      if (!openerState.has(opener)) openerState.set(opener, { hidden: opener.hidden, controls: opener.getAttribute('aria-controls'), popup: opener.getAttribute('aria-haspopup') });
      opener.hidden = false;
      opener.setAttribute('aria-haspopup', 'dialog');
      opener.setAttribute('aria-controls', id);
    });
  };
  const initDetails = node => {
    if (details.has(node) || node.closest('form,dialog,[data-club11-info],[data-category-detail]') || node.hasAttribute('data-v12-reveal')) return;
    node.dataset.immersiveDetailsReady = 'true';
    details.set(node, true);
  };
  const scan = (scope = document) => {
    disposeMatching(node => !node.isConnected);
    const find = selector => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
    find('[data-immersive-scene]').forEach(initScene);
    find('dialog[data-immersive-dialog]').forEach(initDialog);
    find('main details,[data-immersive-details]').forEach(initDetails);
    enhanceOpeners(document);
    schedule();
  };
  function synchronize() {
    const next = settings();
    const nextEffective = allowed(next);
    const changed = !currentSettings || Object.keys(next).some(key => next[key] !== currentSettings[key]);
    if (nextEffective !== effective || changed) {
      effective = nextEffective;
      currentSettings = next;
      body.dataset.immersiveEffective = String(effective);
      body.style.setProperty('--immersive-duration', `${next.duration}ms`);
      if (!effective) {
        for (const record of scenes.values()) resetScene(record);
        for (const animation of animations) cancelAnimation(animation);
        for (const record of dialogs.values()) if (record.node.dataset.immersiveDialogState === 'closing') finishClose(record);
      }
    }
  }
  const disposeMatching = matches => {
    for (const record of scenes.values()) if (matches(record.node)) destroyScene(record);
    for (const record of dialogs.values()) if (matches(record.node)) destroyDialog(record);
    for (const node of details.keys()) if (matches(node)) { node.removeAttribute('data-immersive-details-ready'); details.delete(node); }
    for (const [opener, original] of openerState) if (matches(opener)) {
      opener.hidden = original.hidden;
      for (const [name, value] of [['aria-controls', original.controls], ['aria-haspopup', original.popup]]) value === null ? opener.removeAttribute(name) : opener.setAttribute(name, value);
      openerState.delete(opener);
    }
  };
  const disposeScope = scope => disposeMatching(node => scope.contains(node));
  const destroy = () => {
    if (disposed) return;
    disposed = true;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    disposeMatching(() => true);
    observer?.disconnect();
    cleanups.forEach(remove => remove());
    for (const animation of animations) cancelAnimation(animation);
    body.removeAttribute('data-immersive-effective');
    body.style.removeProperty('--immersive-duration');
    delete window.ITPImmersiveV14;
  };
  window.ITPImmersiveV14 = { refresh: scan, destroy };
  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const record = scenes.get(entry.target);
        if (!record) continue;
        if (entry.isIntersecting) near.add(record);
        else near.delete(record);
      }
      schedule();
    }, { rootMargin: '100% 0px', threshold: .01 });
  }
  listen(document, 'click', event => {
    const close = event.target.closest?.('[data-immersive-dialog-close],[data-modal-close]');
    if (close) { const record = dialogs.get(close.closest('dialog')); if (record) { event.preventDefault(); closeDialog(record); } return; }
    const opener = event.target.closest?.('[data-immersive-dialog-open],[data-modal],[data-modal-image]');
    if (!opener || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const id = dialogId(opener) || (opener.hasAttribute('data-modal-image') ? 'ITPImmersiveImage' : '');
    if (openDialog(findDialog(id), opener)) event.preventDefault();
  });
  listen(window, 'scroll', schedule, { passive: true });
  listen(window, 'resize', schedule, { passive: true });
  listen(document, 'focusin', schedule);
  listen(document, 'focusout', schedule);
  listen(document, 'visibilitychange', () => {
    if (document.hidden) {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      for (const record of dialogs.values()) if (record.node.dataset.immersiveDialogState === 'closing') finishClose(record);
      for (const animation of animations) cancelAnimation(animation);
    } else schedule();
  });
  listen(window, 'pagehide', () => { frozen = true; if (frame) cancelAnimationFrame(frame); frame = 0; for (const animation of animations) cancelAnimation(animation); });
  listen(window, 'pageshow', () => { frozen = false; synchronize(); schedule(); });
  listen(document, 'shopify:section:load', event => scan(event.target));
  listen(document, 'shopify:section:unload', event => disposeScope(event.target));
  const selectInEditor = (event, selected) => {
    for (const record of scenes.values()) if (event.target.contains(record.node) || record.node.contains(event.target)) { record.selected = selected; if (selected) resetScene(record); }
    schedule();
  };
  listen(document, 'shopify:section:select', event => selectInEditor(event, true));
  listen(document, 'shopify:section:deselect', event => selectInEditor(event, false));
  listen(document, 'shopify:block:select', event => selectInEditor(event, true));
  listen(document, 'shopify:block:deselect', event => selectInEditor(event, false));
  const preferenceObserver = new MutationObserver(() => { synchronize(); schedule(); });
  preferenceObserver.observe(html, { attributes: true, attributeFilter: ['data-motion', 'data-reading-calm', 'data-reading-size'] });
  preferenceObserver.observe(body, { attributes: true, attributeFilter: ['data-immersive-enabled', 'data-immersive-intensity', 'data-immersive-duration', 'data-immersive-depth', 'data-immersive-quality', 'class'] });
  cleanups.push(() => preferenceObserver.disconnect());
  synchronize();
  scan();
})();
